import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, FileText, CheckCircle, ArrowRight, ArrowLeft, Car } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AddressAutocomplete from '../../components/maps/AddressAutocomplete';
import Card, { CardContent } from '../../components/ui/Card';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import appConfig from '../../config/appConfig';

const OwnerSetup = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    businessType: user?.businessType || 'both',
    travelsName: user?.travelsInfo?.name || '',
    travelsPhone: user?.travelsInfo?.phone || user?.phone || '',
    travelsEmail: user?.travelsInfo?.email || user?.email || '',
    street: '',
    landmark: '',
    city: user?.travelsInfo?.address?.city || '',
    state: user?.travelsInfo?.address?.state || '',
    pincode: '',
    latitude: null,
    longitude: null,
    gstNumber: '',
    panNumber: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      if (!formData.travelsName.trim()) {
        setError('Company/Travels name is required');
        return;
      }
    }
    if (step === 2) {
      if (!formData.city.trim() || !formData.state.trim()) {
        setError('City and State are required');
        return;
      }
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.COMPLETE_SETUP, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Update user in localStorage and context
        localStorage.setItem('user', JSON.stringify(data.user));
        await refreshUser();
        navigate('/dashboard');
      } else {
        setError(data.message || 'Setup failed');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Business Info', icon: Building2 },
    { num: 2, title: 'Address', icon: MapPin },
    { num: 3, title: 'Documents', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={appConfig.logo} alt="Wheels4Rent" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to {appConfig.name}!</h1>
          <p className="text-gray-600">Let's set up your business profile</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((s, index) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${
                step >= s.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s.num ? <CheckCircle className="h-5 w-5" /> : s.num}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-1 mx-2 rounded ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card>
          <CardContent className="p-8">
            {/* Step 1: Business Info */}
            {step === 1 && (
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
                </div>

                {/* Business type — drives which features this owner sees */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What does your business do? *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'rental', label: 'Renting', desc: 'Rent out vehicles', Icon: Car },
                      { value: 'sale', label: 'Selling', desc: 'Sell vehicles', Icon: FileText },
                      { value: 'both', label: 'Both', desc: 'Rent & sell', Icon: Building2 },
                    ].map(({ value, label, desc, Icon }) => (
                      <button
                        type="button"
                        key={value}
                        onClick={() => setFormData(prev => ({ ...prev, businessType: value }))}
                        className={`flex flex-col items-center text-center gap-1 p-3 rounded-xl border-2 transition-all ${
                          formData.businessType === value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`h-5 w-5 ${formData.businessType === value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-sm font-semibold ${formData.businessType === value ? 'text-blue-700' : 'text-gray-700'}`}>{label}</span>
                        <span className="text-[11px] text-gray-400 leading-tight">{desc}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">You can change this later in Settings.</p>
                </div>

                <Input
                  label="Company / Travels Name *"
                  name="travelsName"
                  type="text"
                  placeholder="e.g., ABC Travels"
                  value={formData.travelsName}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="Business Phone"
                  name="travelsPhone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={formData.travelsPhone}
                  onChange={handleChange}
                />

                <Input
                  label="Business Email"
                  name="travelsEmail"
                  type="email"
                  placeholder="business@example.com"
                  value={formData.travelsEmail}
                  onChange={handleChange}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    placeholder="Brief description of your business..."
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none resize-none"
                    maxLength={500}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Business Address</h3>
                </div>

                <div>
                  <AddressAutocomplete
                    label="Search Address"
                    placeholder="Search your business address..."
                    onChange={(loc) => {
                      setFormData(prev => ({
                        ...prev,
                        street: loc.address || '',
                        city: loc.city || '',
                        state: loc.state || '',
                        pincode: loc.pincode || '',
                        latitude: loc.lat || null,
                        longitude: loc.lng || null
                      }));
                    }}
                  />
                  <p className="text-xs text-orange-500 mt-1 font-medium">Customers will find your vehicles based on this address. Please provide your correct business location.</p>
                </div>

                <Input
                  label="Street Address"
                  name="street"
                  type="text"
                  placeholder="123, Main Road"
                  value={formData.street}
                  onChange={handleChange}
                />

                <Input
                  label="Landmark"
                  name="landmark"
                  type="text"
                  placeholder="Near bus stand"
                  value={formData.landmark}
                  onChange={handleChange}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="City *"
                    name="city"
                    type="text"
                    placeholder="Bangalore"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="State *"
                    name="state"
                    type="text"
                    placeholder="Karnataka"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>

                <Input
                  label="Pincode"
                  name="pincode"
                  type="text"
                  placeholder="560001"
                  value={formData.pincode}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* Step 3: Documents / Tax Info */}
            {step === 3 && (
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <FileText className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Tax & Documents (Optional)</h3>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  You can skip this and add later from Settings.
                </p>

                <Input
                  label="GST Number"
                  name="gstNumber"
                  type="text"
                  placeholder="22AAAAA0000A1Z5"
                  value={formData.gstNumber}
                  onChange={handleChange}
                />

                <Input
                  label="PAN Number"
                  name="panNumber"
                  type="text"
                  placeholder="ABCDE1234F"
                  value={formData.panNumber}
                  onChange={handleChange}
                />

                {/* Summary */}
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-blue-900 mb-2">Setup Summary</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><span className="font-medium">Business:</span> {formData.travelsName}</p>
                    <p><span className="font-medium">Location:</span> {formData.city}, {formData.state}</p>
                    {formData.travelsPhone && <p><span className="font-medium">Phone:</span> {formData.travelsPhone}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 flex items-start">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button type="button" onClick={handleSubmit} disabled={loading}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {loading ? 'Completing Setup...' : 'Complete Setup'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skip option */}
        {step === 3 && (
          <p className="text-center text-sm text-gray-500 mt-4">
            GST & PAN are optional. You can add them later from Settings.
          </p>
        )}
      </div>
    </div>
  );
};

export default OwnerSetup;
